"use client";

import Link from "next/link";
import { useEffect, useState, type ReactNode } from "react";
import { api, type NowPlaying, type ServiceStatus, type SongRequest, type StationConfig, type StatsSample } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { SoonNote, SoonTag, StatsCard } from "@/components/dashboard/stats-ui";
import { formatRelative } from "@/lib/format";
import { RecentActivitiesCard } from "@/components/dashboard/activity-feed";

// The lower part of the Home dashboard: Stations, Quick Actions, Live Requests,
// Recent Activities and System Health. Real: the one station's card, the
// quick-action links, live requests, the activity feed and the service health
// list. "Coming soon" (dashed, badged): extra stations.

const HEALTH_ROWS: { name: string; label: string }[] = [
  { name: "icecast", label: "Icecast Server" },
  { name: "liquidsoap", label: "Liquidsoap (AutoDJ)" },
  { name: "seaweedfs", label: "Media Storage" },
  { name: "postgres", label: "Database" },
  { name: "worker", label: "Background Worker" },
  { name: "api", label: "API Server" },
  { name: "web-dashboard", label: "Web Panel" },
];

type Action = { label: string; href: string; permission?: string; soon?: boolean; icon: ReactNode };

const ACTIONS: Action[] = [
  { label: "Upload Media", href: "/media", permission: "media.view", icon: <><path d="M4 14.9A5 5 0 0 1 6 5.1 6.5 6.5 0 0 1 18.7 8H19a4 4 0 0 1 1 7.9" /><path d="M12 12v9M9 15l3-3 3 3" /></> },
  { label: "Create Playlist", href: "/playlists", permission: "playlists.view", icon: <path d="M3 6h13M3 12h13M3 18h9M19 8v8l-3-3" /> },
  { label: "Manage Schedule", href: "/schedule", permission: "schedule.view", icon: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></> },
  { label: "View Requests", href: "/requests", permission: "requests.view", icon: <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2-5.6A8.5 8.5 0 1 1 21 11.5z" /> },
  { label: "View Reports", href: "/statistics", permission: "statistics.view", icon: <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" /> },
  { label: "Manage Users", href: "/users", permission: "users.view", icon: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18.5 14.5A6.5 6.5 0 0 1 21.5 20" /></> },
];

function Svg({ children, className = "h-5 w-5" }: { children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {children}
    </svg>
  );
}

function LiveRequestsCard() {
  const { can } = useAuth();
  const allowed = can("requests.view");
  const [rows, setRows] = useState<SongRequest[] | null>(null);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    async function load() {
      const [l, s] = await Promise.allSettled([api.requestsList({ status: "pending", limit: 4 }), api.requestsSummary()]);
      if (cancelled) return;
      setRows(l.status === "fulfilled" ? l.value.items : []);
      if (s.status === "fulfilled") setPending(s.value.pending);
    }
    const first = setTimeout(load, 0);
    const poll = setInterval(load, 15000);
    window.addEventListener("requests-changed", load);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(poll);
      window.removeEventListener("requests-changed", load);
    };
  }, [allowed]);

  return (
    <StatsCard
      icon={<path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2-5.6A8.5 8.5 0 1 1 21 11.5z" />}
      iconBg="#9333ea"
      title="Live Requests"
      subtitle="The newest listener requests."
      availability="live"
    >
      {!allowed ? (
        <p className="text-xs text-text-tertiary">Requests need Listener requests access.</p>
      ) : rows === null ? (
        <p className="text-xs text-text-tertiary">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-text-tertiary">No requests waiting. New ones appear here as listeners send them.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-light">
          {rows.slice(0, 3).map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{r.title}</div>
                <div className="truncate text-xs text-text-tertiary">{r.artist}</div>
              </div>
              <div className="shrink-0 text-right text-xs text-text-tertiary">
                <div className="max-w-[90px] truncate">{r.requester_name ?? "Anonymous"}</div>
                <div>{formatRelative(r.created_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {allowed && (
        <Link href="/requests" className="text-xs font-semibold text-accent-blue">
          {pending > 0 ? `Review ${pending} pending →` : "Open requests →"}
        </Link>
      )}
    </StatsCard>
  );
}

export function HomeExtras({ nowPlaying, samples }: { nowPlaying: NowPlaying | null; samples: StatsSample[] }) {
  const { can } = useAuth();
  const [config, setConfig] = useState<StationConfig | null>(null);
  const [services, setServices] = useState<ServiceStatus[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [c, s] = await Promise.allSettled([api.getStationConfig(), api.listServices()]);
      if (cancelled) return;
      if (c.status === "fulfilled") setConfig(c.value);
      setServices(s.status === "fulfilled" ? s.value : null);
    }
    const first = setTimeout(load, 0);
    const poll = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(poll);
    };
  }, []);

  const latest = samples.length ? samples[samples.length - 1] : null;
  const online = latest?.online === true;
  const onAir = online && nowPlaying?.on_air === true;
  const name = config?.name ?? "Station";
  const initials = name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const healthy = services !== null && HEALTH_ROWS.every((r) => services.find((s) => s.name === r.name)?.indicator === "green");

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      {/* Stations */}
      <StatsCard
        className="lg:col-span-2"
        icon={<><circle cx="12" cy="12" r="2" /><path d="M16.2 7.8a6 6 0 0 1 0 8.4M7.8 16.2a6 6 0 0 1 0-8.4M19 5a10 10 0 0 1 0 14M5 19A10 10 0 0 1 5 5" /></>}
        iconBg="#2563eb"
        title="Stations"
        subtitle="Your station and any more you add."
        availability="partial"
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="flex flex-col overflow-hidden rounded-xl border border-border-light">
            <div className="relative flex h-20 items-center justify-center bg-gradient-to-br from-accent-blue to-accent-purple text-2xl font-bold text-white">
              {initials}
              <Link
                href="/listen"
                aria-label={`Listen to ${name}`}
                className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-accent-blue shadow"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
              </Link>
            </div>
            <div className="p-3 text-xs">
              <div className="truncate text-sm font-bold">{name}</div>
              <div className="truncate text-text-secondary">{config?.genre ?? "—"}</div>
              <div className="mt-1 font-semibold text-accent-blue">
                {online ? `${latest?.listeners ?? 0} Listeners` : "Offline"}
                {online && !onAir && <span className="font-normal text-text-tertiary"> · idle</span>}
              </div>
            </div>
          </div>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex min-h-[132px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border-light bg-bg-main/50 text-xs text-text-tertiary"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-border-light text-lg">+</span>
              Add a station
              <SoonTag />
            </div>
          ))}
        </div>
        <SoonNote>Running more than one station arrives with Phase 8.</SoonNote>
      </StatsCard>

      {/* Quick actions */}
      <StatsCard
        icon={<path d="M13 2 4 14h7l-1 8 9-12h-7z" />}
        iconBg="#2563eb"
        title="Quick Actions"
        subtitle="Jump straight to common tasks."
        availability="live"
      >
        <div className="grid grid-cols-3 gap-2">
          {ACTIONS.map((a) => {
            const allowed = a.soon || !a.permission || can(a.permission);
            const body = (
              <>
                <Svg className="h-6 w-6">{a.icon}</Svg>
                <span className="text-[11px] font-medium">{a.label}</span>
                {a.soon && <SoonTag />}
              </>
            );
            return allowed ? (
              <Link
                key={a.label}
                href={a.href}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-center ${
                  a.soon
                    ? "border-dashed border-border-light bg-bg-main/50 text-text-tertiary"
                    : "border-border-light text-accent-blue hover:bg-bg-main"
                }`}
              >
                {body}
              </Link>
            ) : (
              <span
                key={a.label}
                title="You don't have access to this"
                className="flex cursor-not-allowed flex-col items-center gap-1.5 rounded-lg border border-border-light p-3 text-center text-text-tertiary opacity-50"
              >
                {body}
              </span>
            );
          })}
        </div>
      </StatsCard>

      {/* Live requests */}
      <LiveRequestsCard />

      {/* Recent activities */}
      <RecentActivitiesCard />

      {/* System health */}
      <StatsCard
        icon={<path d="M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6z" />}
        iconBg="#16a34a"
        title="System Health"
        subtitle="Core services behind your station."
        availability="live"
      >
        {services === null ? (
          <p className="text-xs text-text-tertiary">Service status needs Monitoring access.</p>
        ) : (
          <>
            <ul className="flex flex-col gap-2 text-xs">
              {HEALTH_ROWS.map((r) => {
                const s = services.find((x) => x.name === r.name);
                const tone = s?.indicator ?? "red";
                return (
                  <li key={r.name} className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${tone === "green" ? "bg-accent-green" : tone === "amber" ? "bg-accent-orange" : "bg-accent-red"}`} />
                    <span className="flex-1">{r.label}</span>
                    <span className={tone === "green" ? "text-accent-green" : tone === "amber" ? "text-accent-orange" : "text-accent-red"}>
                      {tone === "green" ? "Online" : tone === "amber" ? "Degraded" : "Down"}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-between text-xs">
              <span className={healthy ? "font-semibold text-accent-green" : "font-semibold text-accent-orange"}>
                {healthy ? "All systems healthy" : "Needs attention"}
              </span>
              {can("monitoring.view") && (
                <Link href="/monitoring" className="font-semibold text-accent-blue">
                  View Details →
                </Link>
              )}
            </div>
          </>
        )}
      </StatsCard>
    </div>
  );
}
